import { MAX_KM_PER_LITRE, TRAILER_BODY_STYLES, TRUCK_BODY_STYLES, type TrailerHitch, type TruckCoupling, type TyreLayoutSubjectKind } from '@chukua/common';
import { Form, Input, InputNumber, Select } from 'antd';
import { useContainerSizeOptions } from '../../trailer-types/hooks/use-container-size-options';
import { TRAILER_BODY_STYLE_LABELS, TRUCK_BODY_STYLE_LABELS } from '@chukua/vehicle-model';
import { CAPACITY_TONNES_RULES } from '../../../../../shared/policies/capacity-tonnes-rules';

const BODY_OPTIONS: Record<TyreLayoutSubjectKind, { value: string; label: string }[]> = {
  truck_class: TRUCK_BODY_STYLES.map((style) => ({ value: style, label: TRUCK_BODY_STYLE_LABELS[style] })),
  trailer_type: TRAILER_BODY_STYLES.map((style) => ({ value: style, label: TRAILER_BODY_STYLE_LABELS[style] })),
};

const COUPLING_OPTIONS: { value: TruckCoupling; label: string }[] = [
  { value: 'fifth_wheel', label: 'Semi-trailers (fifth wheel)' },
  { value: 'drawbar_hitch', label: 'Drawbar trailers (drawbar hitch)' },
  { value: 'none', label: 'Nothing' },
];

const HITCH_OPTIONS: { value: TrailerHitch; label: string }[] = [
  { value: 'kingpin', label: 'Kingpin, on a fifth wheel' },
  { value: 'drawbar', label: 'Drawbar, on a drawbar hitch' },
];

function TruckClassFields() {
  return (
    <div className="grid grid-cols-2 gap-x-3">
      <Form.Item name="coupling" label="Tows" className="col-span-2" extra="Which trailers a truck of this class can be coupled to.">
        <Select options={COUPLING_OPTIONS} />
      </Form.Item>
      <Form.Item name="typicalCapacityTonnes" label="Typical capacity (t)" rules={CAPACITY_TONNES_RULES}>
        <InputNumber className="w-full" min={0.01} step={0.5} />
      </Form.Item>
      <Form.Item name="requiredLicenceClass" label="Licence class" tooltip="Leave empty when drivers are not checked against a class.">
        <Input className="font-mono" allowClear />
      </Form.Item>
      <Form.Item
        name="defaultKmPerLitre"
        label="Default consumption (km/l)"
        className="col-span-2"
        extra="Where a truck of this class starts until it has enough trips of its own. Leave empty for none."
      >
        <InputNumber className="w-full" min={0.1} max={MAX_KM_PER_LITRE} step={0.1} />
      </Form.Item>
    </div>
  );
}

function TrailerTypeFields() {
  const containerSizeOptions = useContainerSizeOptions(true);
  return (
    <>
      <Form.Item name="hitch" label="Hitch" extra="Which trucks a trailer of this type can be coupled to.">
        <Select options={HITCH_OPTIONS} />
      </Form.Item>
      <Form.Item name="containerSizeCodes" label="Container sizes carried" extra="A job with a container of any other size can't be assigned this trailer.">
        <Select mode="multiple" options={containerSizeOptions} optionFilterProp="label" placeholder="None" />
      </Form.Item>
    </>
  );
}

/**
 * The row's own fields. The code can't change once the row exists: trucks,
 * trailers and the tyre layout all find their class or type by it.
 */
export function VehicleClassDetails({ kind, isNew }: { kind: TyreLayoutSubjectKind; isNew: boolean }) {
  return (
    <section aria-labelledby="vehicle-class-details" className="flex flex-col rounded-lg border border-line bg-surface p-5">
      <h2 id="vehicle-class-details" className="mt-0 mb-3 text-base font-semibold text-fg">
        Details
      </h2>
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3">
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" disabled={!isNew} autoFocus={isNew} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
      </div>
      <Form.Item name="bodyStyle" label="Body">
        <Select options={BODY_OPTIONS[kind]} />
      </Form.Item>
      {kind === 'truck_class' ? <TruckClassFields /> : <TrailerTypeFields />}
    </section>
  );
}
